import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LicenseProvider } from './context/LicenseContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteProvider } from './context/SiteContext';
import { AlarmSocketProvider } from './context/AlarmSocket';
import Login from './pages/Login';
import SOCDashboard from './pages/modules/SOCDashboard';
import SOCSettings from './pages/modules/SOCSettings';
import Video from './pages/modules/Video';
import AccessControl from './pages/modules/AccessControl';
import Assets from './pages/modules/Assets';
import SmartVision from './pages/modules/SmartVision';
import Reports from './pages/modules/Reports';
import CarPark from './pages/modules/CarPark';
import UserManagement from './pages/admin/UserManagement';
import OrgManagement from './pages/admin/OrgManagement';
import SystemHealth from './pages/admin/SystemHealth';
import Workspace from './pages/Workspace';
import Settings from './pages/Settings';
import Jobs from './pages/modules/Jobs';
import ExecutiveDashboard from './pages/modules/ExecutiveDashboard';
import GuardTours from './pages/modules/GuardTours';
import LemaiFC from './pages/modules/LemaiFC';
import ContractorPortal from './pages/modules/ContractorPortal';
import Bookings from './pages/modules/Bookings';
import PublicBookingPortal from './pages/modules/PublicBooking';
import BookingKiosk from './pages/modules/BookingKiosk';
import SitesProgramming from './pages/modules/SitesProgramming';
import ComingSoon from './pages/ComingSoon';
import './styles/design-system.css';
import { AlarmPopupProvider } from './context/AlarmPopup';
import { AuditProvider } from './context/AuditContext';
import { ToastProvider } from './context/ToastContext';
import OnboardingWizard from './pages/OnboardingWizard';

// ── Error Boundary ─────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; msg: string }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, msg: '' };
  }
  static getDerivedStateFromError(err: Error) {
    return { hasError: true, msg: err?.message || 'Unknown error' };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Lemai UI error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#0a0f1e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
          <div style={{ fontSize: '40px' }}>⚠</div>
          <p style={{ color: '#e2e8f0', fontSize: '18px', fontWeight: 600 }}>Something went wrong</p>
          <p style={{ color: '#555555', fontSize: '13px', maxWidth: '400px', textAlign: 'center' }}>{this.state.msg}</p>
          <button
            onClick={() => { this.setState({ hasError: false, msg: '' }); window.location.reload(); }}
            style={{ marginTop: '8px', padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Session expiry warning banner ─────────────────────────────────────────────
function SessionWarning() {
  const { sessionExpiringSoon, extendSession, logout } = useAuth();
  if (!sessionExpiringSoon) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: '#b45309', color: '#fff', padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: '13px', fontWeight: 500, gap: '12px',
    }}>
      <span>Your session expires in ~5 minutes. Stay logged in?</span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={extendSession}
          style={{ padding: '4px 14px', background: '#fff', color: '#b45309', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '12px' }}
        >
          Stay Logged In
        </button>
        <button
          onClick={logout}
          style={{ padding: '4px 12px', background: 'transparent', color: '#fde68a', border: '1px solid #fde68a', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
        >
          Log Out
        </button>
      </div>
    </div>
  );
}

// ── Auth guard ────────────────────────────────────────────────────────────────
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{minHeight:'100vh',background:'#0a0f1e',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:'44px',height:'44px',border:'3px solid #1e3a5f',borderTop:'3px solid #3b82f6',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 14px'}} />
        <p style={{color:'#555555',fontSize:'14px'}}>Loading Lemai...</p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
  return user ? <>{children}</> : <Navigate to="/login" />;
}

// ── Contractor route — only accessible to contractors ─────────────────────────
function ContractorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  return <>{children}</>;
}

// ── Admin-only route guard (super_admin or admin) ─────────────────────────────
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'operator' || user.role === 'contractor') return <Navigate to="/m/soc" />;
  return <>{children}</>;
}

// ── Super-admin-only route guard ──────────────────────────────────────────────
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!user.is_super_admin) return <Navigate to="/m/soc" />;
  return <>{children}</>;
}

// ── Module route guard — redirects to /coming-soon if module not licensed ─────
function ModuleRoute({ slug, children }: { slug: string; children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!(user as any).is_super_admin) {
    const licensed: string[] = (user as any).licensed_modules || [];
    if (!licensed.includes(slug)) return <Navigate to="/coming-soon" />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <LicenseProvider>
      <ThemeProvider>
      <ToastProvider>
      <AuditProvider>
      <SiteProvider>
      <AlarmSocketProvider>
      <AlarmPopupProvider>
      <BrowserRouter>
        <SessionWarning />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/workspace" element={<PrivateRoute><Workspace /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
          <Route path="/coming-soon" element={<PrivateRoute><ComingSoon /></PrivateRoute>} />
          <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
          <Route path="/admin/orgs" element={<SuperAdminRoute><OrgManagement /></SuperAdminRoute>} />
          <Route path="/admin/system-health" element={<SuperAdminRoute><SystemHealth /></SuperAdminRoute>} />
          <Route path="/m/soc" element={<ModuleRoute slug="soc"><SOCDashboard /></ModuleRoute>} />
          <Route path="/soc-settings" element={<AdminRoute><SOCSettings /></AdminRoute>} />
          <Route path="/m/jobs" element={<ModuleRoute slug="jobs"><Jobs /></ModuleRoute>} />
          <Route path="/m/executive" element={<ModuleRoute slug="executive"><ExecutiveDashboard /></ModuleRoute>} />
          <Route path="/m/video" element={<ModuleRoute slug="video"><Video /></ModuleRoute>} />
          <Route path="/m/access" element={<ModuleRoute slug="access"><AccessControl /></ModuleRoute>} />
          <Route path="/m/incidents" element={<Navigate to="/m/jobs" />} />
          <Route path="/m/assets" element={<ModuleRoute slug="assets"><Assets /></ModuleRoute>} />
          <Route path="/m/vision" element={<ModuleRoute slug="vision"><SmartVision /></ModuleRoute>} />
          <Route path="/m/reports" element={<ModuleRoute slug="reports"><Reports /></ModuleRoute>} />
          <Route path="/m/carpark" element={<ModuleRoute slug="carpark"><CarPark /></ModuleRoute>} />
          <Route path="/m/integrations" element={<Navigate to="/settings" />} />
          <Route path="/m/tours" element={<ModuleRoute slug="tours"><GuardTours /></ModuleRoute>} />
          <Route path="/m/bookings" element={<ModuleRoute slug="bookings"><Bookings /></ModuleRoute>} />
          <Route path="/m/face" element={<ModuleRoute slug="face"><LemaiFC /></ModuleRoute>} />
          <Route path="/m/sites" element={<PrivateRoute><SitesProgramming /></PrivateRoute>} />
          <Route path="/contractor" element={<ContractorRoute><ContractorPortal /></ContractorRoute>} />
          {/* Redirects for absorbed/deprecated module routes */}
          <Route path="/m/loneworker" element={<Navigate to="/m/jobs" />} />
          <Route path="/m/floorplans" element={<Navigate to="/m/assets" />} />
          <Route path="/m/intercom" element={<Navigate to="/m/access" />} />
          <Route path="/m/handover" element={<Navigate to="/m/jobs" />} />
          <Route path="/m/suppression" element={<Navigate to="/m/soc" />} />
          <Route path="/m/events" element={<Navigate to="/m/soc" />} />
          <Route path="/book/*" element={<PublicBookingPortal />} />
          <Route path="/kiosk" element={<BookingKiosk />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/" element={<Navigate to="/m/soc" />} />
          <Route path="*" element={<Navigate to="/m/soc" />} />
        </Routes>
      </BrowserRouter>
      </AlarmPopupProvider>
      </AlarmSocketProvider>
      </SiteProvider>
      </AuditProvider>
      </ToastProvider>
      </ThemeProvider>
      </LicenseProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
